import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import {
  ControlButtonGroup,
  ControlButtonGroupName,
  HtmlNotesPreview,
  LanguageUtils,
  LinkButton,
  PermissionArea,
  Text,
  Utils,
} from '@pbt/pbt-ui-components'

import BusinessShareIcon from '~/components/common/icons/BusinessShareIcon'
import ControlledSubstanceLabel from '~/components/common/labels/ControlledSubstanceLabel'
import RxStatusChip from '~/components/common/labels/RxStatusChip'
import Link from '~/components/common/link/Link'
import ChewyPrescriptionPdfButton from '~/components/dashboard/prescription/ChewyPrescriptionPdfButton'
import PrescriptionChewyOrderInfo from '~/components/dashboard/prescription/prescription-editor/PrescriptionChewyOrderInfo'
import PrescriptionDetails from '~/components/dashboard/prescription/prescription-editor/PrescriptionDetails'
import FeatureToggle from '~/constants/featureToggle'
import {
  useChewyPrescriptionStateLabelProps,
  useGetApprovalOnBehalfOfString,
} from '~/store/hooks/prescription'
import { getCRUDByArea } from '~/store/reducers/auth'
import {
  getFeatureToggle,
  getPrescriptionStates,
} from '~/store/reducers/constants'
import type { PrescriptionTimelineItem } from '~/types'
import {
  getPrescriptionType,
  getPrescriptionTypeLabel,
  getPrescriptionWorkflowType,
} from '~/utils/prescription'
import {
  getIdFromTimelineItem,
  hasRefillChangedFromParentPrescription,
} from '~/utils/prescriptionUtils'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import TimelineCard, { type TimelineCardProps } from '../TimelineCard'
import TimelineCardActions from '../TimelineCardActions'
import TimelineCardContent from '../TimelineCardContent'
import PrescriptionsCardDetails from './PrescriptionsCardDetails'
import {
  ReviewApproveButton,
  useShowReviewApproveButton,
} from './rxTracker/ReviewApproveButton'
import {
  getPrescriptionWithOrigin,
  isDispensedFromInventory,
} from './rxTracker/rxCardUtilities'
import { useDispenseRefill } from './rxTracker/useDispenseRefill'
import { usePrescriptionCardActions } from './usePrescriptionCardActions'

const useStyles = makeStyles(
  (theme) => ({
    substanceLabelRoot: {
      position: 'absolute',
      width: 165,
      right: 12,
      top: 12,
    },
    controlled: {
      fontSize: '1.4rem',
    },
    section: {
      '&:nth-of-type(odd)': {
        backgroundColor: theme.colors.tableEvenItem,
      },
    },
    detailsFooter: {
      padding: theme.spacing(1, 2),
      borderTop: theme.constants.tableBorder,
    },
    iconButton: {
      margin: 0,
    },
    refillActions: {
      marginLeft: 'auto',
    },
    dispenseRefillAction: {
      marginLeft: theme.spacing(2),
    },
  }),
  { name: 'PrescriptionsCard' },
)

interface PrescriptionsCardProps extends TimelineCardProps {
  index: number
  isInvoice?: boolean
  item: PrescriptionTimelineItem
}

const PrescriptionsCard = ({
  item: itemProp,
  isInvoice,
  index,
  ...rest
}: PrescriptionsCardProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'Timeline', 'Tasks'])

  const { create: prescriptionCreatePermissions } = useSelector(
    getCRUDByArea(PermissionArea.PRESCRIPTION),
  )
  const { create: invoiceCreatePermissions } = useSelector(
    getCRUDByArea(PermissionArea.INVOICE),
  )
  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )

  const item = getPrescriptionWithOrigin(itemProp)

  const isContextItem = useIsCurrentContextItem(item)

  const refillsCount = item.prescription.refills?.length || 0
  const canceledRefills = Boolean(item.prescription.cancelledBy)
  const { isInHouse } = getPrescriptionType(item.prescription.prescriptionType)

  const prescriptionStates = useSelector(getPrescriptionStates)

  const {
    handlePrint,
    handleViewInvoice,
    handleViewRefill,
    handlePreviewPrescription,
    handlePreviewRefill,
    handleCancelRefills,
    getActionVisibility,
    getSoapUrl,
    persistClickedItem,
  } = usePrescriptionCardActions({
    item,
    index,
    isFoodCatalogEnabled,
    isInvoice,
  })

  const handleDispenseRefill = useDispenseRefill(item)

  const refillsRemaining = item.prescription.quantityNumbRefills
    ? Math.max(item.prescription.quantityNumbRefills - refillsCount, 0)
    : 0

  const prescriptionTypeLabel = getPrescriptionTypeLabel({
    prescriptionType: item.prescription.prescriptionType,
    origin: item.prescription.origin,
  })
  const title = `${prescriptionTypeLabel}–${LanguageUtils.getTranslatedFieldName(
    item,
  )}`

  const stateLabelProps = useChewyPrescriptionStateLabelProps(
    item.prescription,
    item.cancellationReasonDetail,
  )

  const { isChewyReactiveRx, isChewyActiveRx } = getPrescriptionWorkflowType({
    orderType: item.prescription.type,
    prescriptionType: item.prescription.prescriptionType,
    origin: item.prescription.origin,
  })

  const isChewy = isChewyReactiveRx || isChewyActiveRx

  const doctor = item.prescription.doctor ?? item.vet

  const approvalOnBehalfOfString = useGetApprovalOnBehalfOfString({
    doctor,
    showDoctorOnly: !isChewy,
    signerId: item.signerId,
  })

  const prescriptionStateName = Utils.getConstantName(
    item.prescription.stateId,
    prescriptionStates,
  )

  const shouldShowApproveButton = useShowReviewApproveButton(
    item.prescription,
    prescriptionStateName,
  )

  const prescriptionVisibility = getActionVisibility()

  const approvalButton = shouldShowApproveButton ? (
    <ReviewApproveButton
      prescription={item.prescription}
      prescriptionStateName={prescriptionStateName}
    />
  ) : undefined

  const rxStatusChip = shouldShowApproveButton ? (
    <RxStatusChip
      prescription={item.prescription}
      prescriptionStates={prescriptionStates}
    />
  ) : undefined

  return (
    <TimelineCard
      noMarginBottom
      dateLabel={rxStatusChip}
      originBusinessId={item.businessId}
      title={title}
      {...item}
      {...rest}
    >
      <TimelineCardContent noTypography>
        {item.controlled && (
          <ControlledSubstanceLabel
            classes={{
              root: classes.substanceLabelRoot,
              controlled: classes.controlled,
            }}
          />
        )}
        {doctor && <Text strong>{approvalOnBehalfOfString}</Text>}

        <PrescriptionDetails
          showDispensed
          showRefills
          prescription={item.prescription}
        />
        {isChewy && (
          <Grid pt={2}>
            <PrescriptionChewyOrderInfo prescription={item.prescription} />
          </Grid>
        )}
      </TimelineCardContent>
      <PrescriptionsCardDetails
        approvalAction={approvalButton}
        date={item.prescription.creationDate}
        inHousePrescription={isInHouse}
        user={item.prescription.filledBy}
      >
        {prescriptionVisibility.shouldShowSoap && item.soapId && (
          <Link
            to={getSoapUrl(item.soapId)}
            onClick={() => persistClickedItem(index)}
          >
            {t('Common:VIEW_SOAP')}
          </Link>
        )}
        {prescriptionVisibility.canShowMenu &&
          prescriptionVisibility.shouldShowPreview && (
            <ControlButtonGroup
              buttons={[
                prescriptionVisibility.shouldShowPrint && {
                  name: ControlButtonGroupName.PRINT,
                  onClick: () => handlePrint(item.prescription),
                },
                prescriptionVisibility.shouldShowInvoice && {
                  name: ControlButtonGroupName.ESTIMATES,
                  onClick: () => handleViewInvoice(getIdFromTimelineItem(item)),
                },
                {
                  name: ControlButtonGroupName.PREVIEW,
                  onClick: handlePreviewPrescription,
                },
              ]}
              classes={{
                iconButton: classes.iconButton,
              }}
            />
          )}
      </PrescriptionsCardDetails>
      {item.prescription.refills?.map((refill, _refillIndex) => {
        const hasChanged = hasRefillChangedFromParentPrescription(
          item.prescription,
          refill,
        )

        const refillVisibility = getActionVisibility(refill)

        return (
          <Grid className={classes.section} key={refill.id || _refillIndex}>
            <TimelineCardContent noTypography>
              <>
                <Grid container item alignItems="center" mt={2}>
                  <Text strong>{`${t('Common:REFILL_ONE')} ${
                    refill.refillOrdinalNumber
                  }`}</Text>
                  <BusinessShareIcon
                    businessIds={refill.businessId ? [refill.businessId] : []}
                  />
                </Grid>
                {hasChanged ? (
                  <PrescriptionDetails showDispensed prescription={refill} />
                ) : (
                  refill.notes && (
                    <HtmlNotesPreview
                      includeLabel
                      fontSize="1.4rem"
                      label={t(
                        'Clients:TIMELINE.PRESCRIPTION_CARD.ADMINISTRATION_NOTES',
                      )}
                      notes={refill.notes}
                    />
                  )
                )}
              </>
            </TimelineCardContent>
            <PrescriptionsCardDetails
              refill
              date={refill.creationDate}
              inHousePrescription={isDispensedFromInventory(refill)}
              user={refill.filledBy}
            >
              {refillVisibility.canShowMenu &&
                refillVisibility.shouldShowPreview && (
                  <ControlButtonGroup
                    buttons={[
                      refillVisibility.shouldShowPrint && {
                        name: ControlButtonGroupName.PRINT,
                        onClick: () =>
                          handlePrint({ ...refill, parent: item.prescription }),
                      },
                      refillVisibility.shouldShowInvoice && {
                        name: ControlButtonGroupName.ESTIMATES,
                        onClick: () => handleViewRefill(refill),
                      },
                      {
                        name: ControlButtonGroupName.PREVIEW,
                        onClick: () => handlePreviewRefill(refill),
                      },
                    ]}
                    classes={{
                      iconButton: classes.iconButton,
                    }}
                  />
                )}
            </PrescriptionsCardDetails>
          </Grid>
        )
      })}
      {canceledRefills && (
        <PrescriptionsCardDetails
          cancelledRefills
          date={item.prescription.refillCancelDate}
          inHousePrescription={isInHouse}
          user={item.prescription.cancelledBy}
        />
      )}
      {refillsRemaining > 0 && !canceledRefills && isInHouse && (
        <TimelineCardContent noTypography className={classes.detailsFooter}>
          <Grid container alignItems="center">
            <Text strong>
              {t('Plurals:Z_ICU_WORKAROUND.REFILL_REMAINING', {
                refillsRemaining,
              })}
            </Text>
            {isContextItem && (
              <Grid item className={classes.refillActions}>
                <LinkButton onClick={handleCancelRefills}>
                  {t('Clients:TIMELINE.PRESCRIPTION_CARD.CANCEL_ALL_REFILLS')}
                </LinkButton>
                <LinkButton
                  className={classes.dispenseRefillAction}
                  disabled={
                    !invoiceCreatePermissions && !prescriptionCreatePermissions
                  }
                  onClick={handleDispenseRefill}
                >
                  {t('Common:DISPENSE_REFILL')}
                </LinkButton>
              </Grid>
            )}
          </Grid>
        </TimelineCardContent>
      )}
      {!shouldShowApproveButton && (
        <TimelineCardActions
          {...item}
          errorState={stateLabelProps.error}
          state={stateLabelProps.children}
          warningState={stateLabelProps.warning}
        >
          {isChewy && (
            <span>
              <ChewyPrescriptionPdfButton pdfUrl={item.prescription.pdfUrl} />
            </span>
          )}
        </TimelineCardActions>
      )}
    </TimelineCard>
  )
}

export default PrescriptionsCard
